/**
 * IntegrationCard — a single integration card in the Settings page.
 *
 * Handles:
 *   - Connected / disconnected status pill
 *   - Google OAuth flow (shared connect button for Gmail + Drive)
 *   - API-key write-only input (Asana, MLS)
 *   - Disconnect button for any connected integration
 *   - Per-card error display
 *   - Loading / action states
 */

import React, { useState } from "react";
import type { Integration } from "./types.js";

// ─── Icon components (inline SVG — no icon font dependency) ──────────────────

function GmailIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <rect width="24" height="24" rx="3" fill="none" />
      <path d="M2 6l10 7 10-7" stroke="#EA4335" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="2" y="5" width="20" height="14" rx="2" stroke="#EA4335" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function GDriveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M8 19l-5-9 4-7h10l4 7-5 9H8z" stroke="#4285F4" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
      <path d="M3 10l18 0" stroke="#FBBC04" strokeWidth="1.5" />
      <path d="M12 3L7 12" stroke="#34A853" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AsanaIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <circle cx="12" cy="7" r="4" fill="#F06A6A" />
      <circle cx="6" cy="17" r="4" fill="#F06A6A" opacity="0.75" />
      <circle cx="18" cy="17" r="4" fill="#F06A6A" opacity="0.75" />
    </svg>
  );
}

function MLSIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

const ICONS: Record<string, React.ReactElement> = {
  gmail: <GmailIcon />,
  gdrive: <GDriveIcon />,
  asana: <AsanaIcon />,
  mls: <MLSIcon />,
};

const API_KEY_PLACEHOLDER: Record<string, string> = {
  asana: "Personal Access Token",
  mls: "MLS API Key",
};

// ─── Status pill ──────────────────────────────────────────────────────────────

interface StatusPillProps {
  status: "connected" | "disconnected";
}

function StatusPill({ status }: StatusPillProps) {
  const connected = status === "connected";
  return (
    <span
      className={`settings-status-pill settings-status-pill--${status}`}
      role="status"
      aria-label={connected ? "Connected" : "Not connected"}
    >
      <span className="settings-status-pill__dot" aria-hidden="true" />
      {connected ? "Connected" : "Not connected"}
    </span>
  );
}

// ─── IntegrationCard ──────────────────────────────────────────────────────────

export interface IntegrationCardProps {
  integration: Integration;
  /** Current action in progress for this card, or null. */
  actionState: string | null;
  /** Inline error message for this card, or null. */
  cardError: string | null;
  /**
   * Whether the shared Google OAuth connection is active (for gmail/gdrive).
   * Kept in props for future use / parent awareness; currently each card
   * derives its own connected state from integration.status.
   */
  googleConnected: boolean;
  onGoogleConnect: () => Promise<void>;
  onApiKeySave: (key: string, value: string) => Promise<void>;
  onDisconnect: (key: string) => Promise<void>;
}

export function IntegrationCard({
  integration,
  actionState,
  cardError,
  googleConnected: _googleConnected, // surfaced to parent; each card uses own status
  onGoogleConnect,
  onApiKeySave,
  onDisconnect,
}: IntegrationCardProps) {
  const { key, label, status, authType, detail } = integration;
  const isConnected = status === "connected";
  const isBusy = actionState !== null;

  // Local API key input value (write-only; never pre-populated with secret)
  const [apiKeyValue, setApiKeyValue] = useState("");
  const inputId = `settings-apikey-${key}`;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKeyValue.trim()) return;
    await onApiKeySave(key, apiKeyValue.trim());
    // Clear the input after successful save (or even on error — write-only)
    setApiKeyValue("");
  };

  const icon = ICONS[key] ?? <MLSIcon />;

  return (
    <article
      className={`settings-card ${isConnected ? "settings-card--connected" : ""}`}
      aria-label={`${label} integration`}
    >
      {/* Top row: icon, name, detail, status pill */}
      <div className="settings-card__top">
        <div className="settings-card__icon" aria-hidden="true">
          {icon}
        </div>

        <div className="settings-card__info">
          <p className="settings-card__name">{label}</p>
          {detail && (
            <p className="settings-card__detail" title={detail}>
              {detail}
            </p>
          )}
          {isConnected && !detail && (
            <p className="settings-card__detail">Connected</p>
          )}
        </div>

        <StatusPill status={status} />
      </div>

      {/* Card error */}
      {cardError && (
        <p className="settings-card__error" role="alert">
          {cardError}
        </p>
      )}

      {/* Actions */}
      <div className="settings-card__actions">
        {authType === "oauth" ? (
          /* Google OAuth card actions */
          isConnected ? (
            <div className="settings-card__btn-row">
              <button
                type="button"
                className="settings-btn settings-btn--danger"
                onClick={() => void onDisconnect(key)}
                disabled={isBusy}
                aria-label={`Disconnect ${label}`}
              >
                {actionState === "disconnecting" ? (
                  <span className="settings-spinner" aria-hidden="true">↺</span>
                ) : null}
                Disconnect
              </button>
            </div>
          ) : (
            <div className="settings-card__btn-row">
              <button
                type="button"
                className="settings-btn settings-btn--primary"
                onClick={() => void onGoogleConnect()}
                disabled={isBusy}
                aria-label={`Connect Google account for ${label}`}
              >
                {actionState === "connecting" ? (
                  <span className="settings-spinner" aria-hidden="true">↺</span>
                ) : null}
                Connect Google
              </button>
            </div>
          )
        ) : (
          /* API key card actions */
          isConnected ? (
            <div className="settings-card__btn-row">
              <button
                type="button"
                className="settings-btn settings-btn--danger"
                onClick={() => void onDisconnect(key)}
                disabled={isBusy}
                aria-label={`Disconnect ${label}`}
              >
                {actionState === "disconnecting" ? (
                  <span className="settings-spinner" aria-hidden="true">↺</span>
                ) : null}
                Disconnect
              </button>
            </div>
          ) : (
            <form
              className="settings-apikey-row"
              onSubmit={(e) => void handleSave(e)}
              aria-label={`${label} API key`}
            >
              <label htmlFor={inputId} className="sr-only">
                {API_KEY_PLACEHOLDER[key] ?? "API Key"}
              </label>
              <input
                id={inputId}
                type="password"
                className="settings-apikey-input"
                placeholder={API_KEY_PLACEHOLDER[key] ?? "API Key"}
                value={apiKeyValue}
                onChange={(e) => setApiKeyValue(e.target.value)}
                autoComplete="off"
                aria-label={`${label} ${API_KEY_PLACEHOLDER[key] ?? "API Key"}`}
                disabled={isBusy}
              />
              <button
                type="submit"
                className="settings-btn settings-btn--secondary"
                disabled={isBusy || !apiKeyValue.trim()}
                aria-label={`Save ${label} credentials`}
              >
                {actionState === "saving" ? (
                  <span className="settings-spinner" aria-hidden="true">↺</span>
                ) : null}
                Save
              </button>
            </form>
          )
        )}
      </div>
    </article>
  );
}
